// src/api/features/users/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  UserResult,
  CreateUserBody,
  UpdateUserBody,
  ListUsersQuery,
  PaginatedUsers,
} from "./types";
import type { Module } from "@/api/features/modules/types";

/* =========================
 * Helpers
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* =========================
 * URLs (constants)
 * ========================= */

const USERS_BASE = PATHS.users.base;

/* =========================
 * Endpoints — CORE USERS
 * ========================= */

/** 🔹 Create user */
export async function createUser(body: CreateUserBody): Promise<UserResult> {
  const dto: any = { ...body };

  // Light normalization / trimming
  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.username != null) dto.username = String(dto.username).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createUser] payload →", dto);
  }

  const res = await request<UserResult | ApiEnvelope<UserResult>>({
    method: "POST",
    url: USERS_BASE,
    data: dto,
  });

  return unwrap<UserResult>(res);
}

/** 🔹 List users (paginated) */
export async function listUsers(
  query: ListUsersQuery = {}
): Promise<PaginatedUsers> {
  // Clamp + sanitize pagination
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawLimit = Number(query.limit ?? 10);
  const safeLimit = Math.max(
    1,
    Math.min(100, Number.isFinite(rawLimit) ? rawLimit : 10)
  );

  const url = `${USERS_BASE}${qs({
    page: safePage,
    limit: safeLimit,
    q: query.q,
    roleId:
      typeof query.roleId === "number" && Number.isFinite(query.roleId)
        ? query.roleId
        : undefined,
  })}`;

  const res = await request<
    PaginatedUsers | (ApiEnvelope<UserResult[]> & { meta?: any })
  >({
    method: "GET",
    url,
  });

  // If server returns { ok, data, meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<UserResult[]> & { meta?: any };

    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? safePage),
      pageSize: Number(meta.pageSize ?? meta.limit ?? safeLimit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  // Otherwise assume backend already returns PaginatedUsers
  return res as PaginatedUsers;
}

/** 🔹 Get user by id */
export async function getUser(id: number | string): Promise<UserResult> {
  const res = await request<UserResult | ApiEnvelope<UserResult>>({
    method: "GET",
    url: `${USERS_BASE}/${id}`,
  });

  return unwrap<UserResult>(res);
}

/** 🔹 Update user */
export async function updateUser(
  id: number | string,
  body: UpdateUserBody
): Promise<UserResult> {
  const dto: any = { ...body };

  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.username != null) dto.username = String(dto.username).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateUser] id=", id, "payload →", dto);
  }

  const res = await request<UserResult | ApiEnvelope<UserResult>>({
    method: "PUT",
    url: `${USERS_BASE}/${id}`,
    data: dto,
  });

  return unwrap<UserResult>(res);
}

/** 🔹 Soft delete user */
export async function deleteUser(
  id: number | string
): Promise<{ message: string }> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${USERS_BASE}/${id}`,
  });

  return unwrap<{ message: string }>(res);
}

/* =========================
 * Endpoints — USER MODULES
 * ========================= */

/** 🔹 List modules a user has access to */
export async function getUserModules(
  userId: number | string
): Promise<Module[]> {
  const res = await request<Module[] | ApiEnvelope<Module[]>>({
    method: "GET",
    url: `${USERS_BASE}/${userId}/modules`,
  });

  return unwrap<Module[]>(res);
}

/** 🔹 Grant module access */
export async function grantUserModules(
  userId: number | string,
  moduleIds: number[]
): Promise<Module[]> {
  const res = await request<Module[] | ApiEnvelope<Module[]>>({
    method: "POST",
    url: `${USERS_BASE}/${userId}/modules/grant`,
    data: { moduleIds },
  });

  return unwrap<Module[]>(res);
}

/** 🔹 Revoke module access */
export async function revokeUserModules(
  userId: number | string,
  moduleIds: number[]
): Promise<Module[]> {
  const res = await request<Module[] | ApiEnvelope<Module[]>>({
    method: "POST",
    url: `${USERS_BASE}/${userId}/modules/revoke`,
    data: { moduleIds },
  });

  return unwrap<Module[]>(res);
}
